import * as React from "react";
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  ReferenceField,
  ReferenceInput,
  DateInput,
  FunctionField,
  WrapperField,
  SelectInput,
  useGetOne,
  Filter,
  FilterProps,
} from "react-admin";
import { Stack, Chip, Typography, Box } from "@mui/material";
import { useParams } from "react-router-dom";
import { SiteHeader } from "../sites/SiteHeader";
import { makeRemovalsBySiteExporter } from "./exporter/removalsBySite.exporter";
import { formatEquipmentLabel } from "../utils/formatEquipment";
import { RemovalPassedFromLabels } from "../common/consts/RemovalPassedFrom.const";
import { RemovalsStatsPanel } from "./RemovalsStatsPanel";
import { SiteSortingRatePanel } from "../sites/SiteSortingRatePanel";

type RemovalsFiltersProps = FilterProps & { siteId: string };

const RemovalsFilters: React.FC<RemovalsFiltersProps> = (props) => {
  const { siteId, ...raProps } = props;
  return (
    <Filter {...raProps}>
      <ReferenceInput
        alwaysOn
        source="equipment"
        reference="equipments"
        filter={{
          filterTree: {
            operator: "and",
            operands: [
              { term: { name: "site", filterType: "eq", value: siteId } },
            ],
          },
        }}
      >
        <SelectInput
          label="Équipement"
          optionText={(choice: any) =>
            choice ? formatEquipmentLabel(choice) : ""
          }
          fullWidth
        />
      </ReferenceInput>
      <DateInput alwaysOn source="createdAt_gte" label="Créé après le" />
      <DateInput alwaysOn source="createdAt_lte" label="Créé avant le" />
    </Filter>
  );
};

export const RemovalsListBySite: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: site, isLoading } = useGetOne("sites", { id: id! });

  if (isLoading) return <Typography>Chargement…</Typography>;
  if (!site) return <Typography>Site introuvable.</Typography>;

  let preFilterValue: any = [];
  if (id) {
    preFilterValue = JSON.stringify([{ name: "site", value: site._id }]);
  }

  return (
    <Box sx={{ p: 2 }}>
      <SiteHeader site={site} />
      <List
        resource="site-inventory"
        title="Déclarations d'enlèvement"
        queryOptions={{
          meta: { preFilter: preFilterValue },
        }}
        filters={<RemovalsFilters siteId={site._id} children={undefined} />}
        exporter={makeRemovalsBySiteExporter(site.internalCode)}
        sort={{ field: "createdAt", order: "DESC" }}
        perPage={25}
      >
        <Datagrid>
          <ReferenceField
            source="equipment"
            reference="equipments"
            label="Équipement"
            link={false}
          >
            <FunctionField render={(eq: any) => formatEquipmentLabel(eq)} />
          </ReferenceField>

          <WrapperField label="Taux de remplissage">
            <FunctionField
              render={(record: any) => {
                const c = record?.counts ?? {};
                return (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                      size="small"
                      variant="outlined"
                      color="default"
                      label={`0%: ${c._0 ?? 0}`}
                    />
                    <Chip
                      size="small"
                      color="error"
                      label={`25%: ${c._25 ?? 0}`}
                    />
                    <Chip
                      size="small"
                      color="warning"
                      label={`50%: ${c._50 ?? 0}`}
                    />
                    <Chip
                      size="small"
                      color="info"
                      label={`75%: ${c._75 ?? 0}`}
                    />
                    <Chip
                      size="small"
                      color="success"
                      label={`100%: ${c._100 ?? 0}`}
                    />
                    <Chip
                      size="small"
                      color="secondary"
                      label={`125%: ${c._125 ?? 0}`}
                    />
                  </Stack>
                );
              }}
            />
          </WrapperField>

          <NumberField
            source="equivalentFullUnits"
            label="Équiv. plein"
            options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
          />
          <NumberField
            source="tonnage"
            label="Équiv. tonnage"
            options={{ minimumFractionDigits: 4, maximumFractionDigits: 4 }}
          />
          <NumberField
            source="quantityAtTheMoment"
            label="Qté equip. (à ce moment)"
            options={{ minimumFractionDigits: 0, maximumFractionDigits: 0 }}
          />

          <TextField source="note" label="Note" />

          <FunctionField
            label="Ajouté Par"
            render={(el) =>
              RemovalPassedFromLabels[el.passedFrom] ?? el.passedFrom ?? "N/A"
            }
          />

          <DateField
            source="scheduledAt"
            label="Date Planning"
            locales="fr-FR"
            options={{
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            }}
          />
          <DateField
            source="createdAt"
            label="Créé le"
            locales="fr-FR"
            options={{
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            }}
          />
        </Datagrid>
      </List>

      <RemovalsStatsPanel siteId={site._id} />

      <SiteSortingRatePanel siteId={site._id} />
    </Box>
  );
};
